/**
 * offerCompensationDefaults — derives the default Offer Compensation %
 * for the CompensationBuilder.
 *
 * Rule (per ops policy, May 2026):
 *   • Default installer compensation = 15% of NET contract.
 *   • NET contract = contract value − assumed finance fee.
 *   • Assumed finance fee defaults to 12% of contract for financed deals.
 *   • For cash deals (paymentPlan.type === "cash" or "card", or no
 *     financing record), finance fee = 0% and net = contract.
 *
 * Pure derivation — no writers, no mutations, no schema. Internal-only;
 * never expose to installer-facing surfaces.
 */
import type { Job } from "@/data/types";

export const DEFAULT_FINANCE_FEE_PCT = 12;
export const DEFAULT_OFFER_PCT_OF_NET = 15;

export type DealKind = "cash" | "financed";

export function resolveDealKind(job: Job): DealKind {
  const planType = job.financials?.paymentPlan?.type;
  if (planType === "cash" || planType === "card") return "cash";
  if (planType === "financed" || planType === "mixed") return "financed";
  const fin = job.financials?.financing;
  if (fin && fin.financingStatus !== "not_applicable") return "financed";
  return "cash";
}

export interface OfferCompensationDefaults {
  contractValue: number;
  dealKind: DealKind;
  financeFeePct: number;
  financeFeeAmount: number;
  netContract: number;
  defaultPct: number;
  defaultAmount: number;
}

export function getOfferCompensationDefaults(job: Job): OfferCompensationDefaults {
  const contractValue = job.financials?.contractValue ?? 0;
  const dealKind = resolveDealKind(job);
  const financeFeePct = dealKind === "financed" ? DEFAULT_FINANCE_FEE_PCT : 0;
  const financeFeeAmount = Math.round((financeFeePct / 100) * contractValue);
  const netContract = Math.max(0, contractValue - financeFeeAmount);
  const defaultPct = DEFAULT_OFFER_PCT_OF_NET;
  const defaultAmount = Math.round((defaultPct / 100) * netContract);
  return {
    contractValue,
    dealKind,
    financeFeePct,
    financeFeeAmount,
    netContract,
    defaultPct,
    defaultAmount,
  };
}
